import { useEffect, useRef, useState } from "react";

import { api } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useMe } from "../context/MeContext.js";
import { WorkScheduleSection, type ShiftRecord } from "./employee-form/sections/WorkScheduleSection.js";
import { SectionCollapseProvider, useSectionCollapseState } from "./employee-form/SectionCollapseContext.js";

/**
 * The employee profile's Work Schedule section, usable anywhere an employee id is
 * in hand -- the attendance team panel, for one, where a manager who has just been
 * told somebody has no shift should be able to set it without leaving the page.
 *
 * WorkScheduleSection itself is fully controlled: every piece of its state lives in
 * the employee profile page. This owns that state and the two fetches instead, so
 * the section is reused rather than copied. Nothing about the editor, its timeline
 * or its save path is duplicated here.
 */
export function WorkScheduleCard({ employeeId, onSaved }: {
  employeeId: string;
  /** Called after a shift is saved, so the surrounding page can refresh. */
  onSaved?: () => void;
}) {
  const { role } = useMe();
  // Without a provider the section falls back to ALWAYS_OPEN, whose toggle is a
  // no-op -- the header chevron would be there and do nothing. Open by default,
  // since this card is shown deliberately rather than as one of thirteen.
  const sectionCollapse = useSectionCollapseState(true);
  const canEditShift = role === "MANAGER" || role === "SR_MANAGER"
    || role === "SUPER_HR_ADMIN" || role === "CORPORATE_HR" || role === "REGIONAL_HR";

  const sectionRef = useRef<HTMLDivElement>(null);
  const shiftDragRef = useRef<HTMLDivElement>(null);

  const [currentShift, setCurrentShift] = useState<ShiftRecord | null>(null);
  const [shiftHistory, setShiftHistory] = useState<ShiftRecord[]>([]);
  const [shiftStartHour, setShiftStartHour] = useState(9);
  const [shiftType, setShiftType] = useState<"MORNING" | "EVENING" | "NIGHT" | "CUSTOM">("MORNING");
  const [shiftEffFrom, setShiftEffFrom] = useState(new Date().toISOString().slice(0, 10));
  const [shiftReason, setShiftReason] = useState("");
  const [shiftSaving, setShiftSaving] = useState(false);
  const [shiftError, setShiftError] = useState<string | null>(null);
  const [shiftHistExpanded, setShiftHistExpanded] = useState(false);

  useEffect(() => {
    let live = true;
    api.get<ShiftRecord | null>(`/employees/${employeeId}/shift/current`)
      .then((r) => {
        if (!live) return;
        setCurrentShift(r.data);
        if (r.data) {
          // Half-hour starts (08:30) are selectable, so keep the minutes.
          const [hh, mm] = r.data.shiftStartHHMM.split(":");
          setShiftStartHour(parseInt(hh ?? "9", 10) + (parseInt(mm ?? "0", 10) >= 30 ? 0.5 : 0));
          setShiftType(r.data.shiftType as "MORNING" | "EVENING" | "NIGHT" | "CUSTOM");
        }
      })
      .catch((err) => logError("WorkScheduleCard.loadCurrent", err, { employeeId }));

    api.get<ShiftRecord[]>(`/employees/${employeeId}/shift/history`)
      .then((r) => { if (live) setShiftHistory(r.data); })
      .catch((err) => logError("WorkScheduleCard.loadHistory", err, { employeeId }));

    return () => { live = false; };
  }, [employeeId]);

  return (
    <SectionCollapseProvider value={sectionCollapse}>
    <WorkScheduleSection
      sectionRef={sectionRef}
      canEditShift={canEditShift}
      employeeId={employeeId}
      ensureEmployee={async () => employeeId}
      currentShift={currentShift}
      setCurrentShift={(v) => { setCurrentShift(v); if (v) onSaved?.(); }}
      shiftHistory={shiftHistory} setShiftHistory={setShiftHistory}
      shiftStartHour={shiftStartHour} setShiftStartHour={setShiftStartHour}
      shiftType={shiftType} setShiftType={setShiftType}
      shiftEffFrom={shiftEffFrom} setShiftEffFrom={setShiftEffFrom}
      shiftReason={shiftReason} setShiftReason={setShiftReason}
      shiftSaving={shiftSaving} setShiftSaving={setShiftSaving}
      shiftError={shiftError} setShiftError={setShiftError}
      shiftHistExpanded={shiftHistExpanded} setShiftHistExpanded={setShiftHistExpanded}
      shiftDragRef={shiftDragRef}
    />
    </SectionCollapseProvider>
  );
}

export default WorkScheduleCard;
